import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithErrorHandling } from "../baseQuery";

export const blogsApi = createApi({
  reducerPath: "blogsApi",
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ["Blogs"],
  refetchOnFocus: true,
  refetchOnReconnect: true,

  endpoints: (builder) => ({
    // Get All Blogs
    getBlogs: builder.query({
      query: ({
        page = 1,
        limit = 10,
        search = "",
        categoryId = "",
        topRead = "",
        status = "",
        sortBy = "published_at",
        order = "desc",
      } = {}) => ({
        url: "/blogs",
        params: {
          page,
          limit,
          search,
          categoryId,
          topRead,
          status,
          sortBy,
          order,
        },
      }),
      transformResponse: (response) => ({
        blogs: response?.data ?? [],
        total: response?.total ?? 0,
        page: response?.page ?? 1,
        limit: response?.limit ?? 10,
      }),
      providesTags: (result) =>
        result?.blogs?.length
          ? [
              ...result.blogs.map(({ id }) => ({
                type: "Blogs",
                id: Number(id),
              })),
              { type: "Blogs", id: "LIST" },
            ]
          : [{ type: "Blogs", id: "LIST" }],
    }),

    // Get Blog By ID
    getBlogById: builder.query({
      query: (id) => `/blogs/${id}`,
      providesTags: (result, error, id) => [{ type: "Blogs", id }],
    }),

    // Get Blog By Slug
    getBlogBySlug: builder.query({
      query: (slug) => `/blogs/slug/${slug}`,
      providesTags: (result, error, slug) => [{ type: "Blogs", id: slug }],
    }),

    // Create Blog
    createBlog: builder.mutation({
      query: (data) => ({
        url: "/blogs",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [{ type: "Blogs", id: "LIST" }],
    }),

    // Update Blog
    updateBlog: builder.mutation({
      query: ({ id, data }) => ({
        url: `/blogs/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Blogs", id },
        { type: "Blogs", id: "LIST" },
      ],
    }),

    // Delete Blog
    deleteBlog: builder.mutation({
      query: (id) => ({
        url: `/blogs/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Blogs", id: "LIST" }],
    }),

    // Toggle Status
    toggleBlogStatus: builder.mutation({
      query: (id) => ({
        url: `/blogs/${id}/toggle`,
        method: "PATCH",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "Blogs", id },
        { type: "Blogs", id: "LIST" },
      ],
    }),

    // Toggle Top Read
    toggleTopRead: builder.mutation({
      query: (id) => ({
        url: `/blogs/${id}/toggle-top-read`,
        method: "PATCH",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "Blogs", id },
        { type: "Blogs", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetBlogsQuery,
  useGetBlogByIdQuery,
  useGetBlogBySlugQuery,
  useCreateBlogMutation,
  useUpdateBlogMutation,
  useDeleteBlogMutation,
  useToggleBlogStatusMutation,
  useToggleTopReadMutation,
} = blogsApi;
